import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Chart, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
  selector: 'app-doughnut',
  templateUrl: './doughnut.component.html',
  styleUrls: ['./doughnut.component.scss'],
})
export class DoughnutComponent implements OnInit, AfterViewInit {
  @ViewChild('dChart') dChartCanvas!: ElementRef<HTMLCanvasElement>;
  private dChartContext!: CanvasRenderingContext2D;
  private dChart: any;
  private chart: any;
  @Input() data: any[] = [];
  @Input() colors = [ '#00855A', '#FED766', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf', '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5', '#c49c94', '#f7b6d2', '#c7c7c7', '#dbdb8d', '#9edae5', '#fdd0a2', '#b3e2cd', '#fccde5', '#d9d9d9', '#e5d8bd', ];
  @Input() chartConfig: {
    tooltip?: 'both' | 'percentage' | 'value';
    dataLabel?: 'both' | 'percentage' | 'value';
    centerText?: boolean;
  } = {
    tooltip: 'both',
    dataLabel: 'both',
    centerText: true
  };
  customData: any = [];
  customLabel: any = [];
  isPercentage = false;
  isProgress = false;
  showNoData = false;
  constructor() {}

  ngOnInit(): void {
    if (this.data) {
      this.getDataForChart(this.data);
      this.getLabelForChart(this.data);
    }
    if (this.chart) {
      this.chart.destroy();
    }
    this.createdChart();
  }

  centerText(chart: any): void {
    if (!chart?.config?.options?.centerText?.show) {
      return;
    }
    const ctx = chart.ctx;
    const width = chart.width;
    const height = chart.height;
    const centerX = width / 2;
    const centerY = height / 2;

    // Calculate total value (assuming single dataset)
    const datasets = chart.config.data.datasets;
    let totalValue = 0;
    datasets[0].data.forEach((value: any) => {
      totalValue += Number(value);
    });

    // Calculate and format percentage
    const percentage =
      chart.config.options.centerText &&
      chart.config.options.centerText.calculate
        ? chart.config.options.centerText.calculate(datasets[0].data).toString()
        : `${((totalValue / totalValue) * 100).toFixed(2)}%`;

    // Draw text
    ctx.save();
    if (chart?.config?.options?.centerText?.title) {
      ctx.font =
        chart?.config?.options?.centerText?.title &&
        chart?.config?.options?.centerText?.title.font
          ? chart?.config?.options?.centerText?.title.font
          : '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle =
        chart?.config?.options?.centerText?.title &&
        chart?.config?.options?.centerText?.title.color
          ? chart?.config?.options?.centerText?.title.color
          : 'black';
      const text = chart?.config?.options?.centerText?.title?.text;
      const yOffset = chart?.config?.options?.centerText?.title?.yOffset || 0;
      const centerYTitle = centerY - 5.5;
      const centerXTitle = centerX - (Number(text.length / 2));
      ctx.fillText(text, centerXTitle, centerYTitle + yOffset);
    }
    ctx.font =
      chart.config.options.centerText && chart.config.options.centerText.font
        ? chart.config.options.centerText.font
        : '16px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle =
      chart.config.options.centerText && chart.config.options.centerText.color
        ? chart.config.options.centerText.color
        : 'black';
    const centerYValue = centerY - 7;
    const centerXValue = centerX - (Number(percentage.length / 2));
    ctx.fillText(percentage, centerXValue, centerYValue);

    ctx.restore();
  }

  private createdChart(): void {
    Chart.register(ChartDataLabels);
    Chart.register({
      id: 'centerText',
      beforeDraw: this.centerText,
    });
    Chart.defaults.set('plugins.datalabels', {
      color: '#fff',
    });
    const numericalData = this.convertIntoPercentage(this.customData);
    this.dChart = new Chart(this.dChartContext, {
      type: 'doughnut',
      data: {
        labels: this.customLabel,
        datasets: [
          {
            data: this.customData,
            backgroundColor: this.isProgress ? ['#00855A', '#e5e5e5'] : this.colors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            display: true,
            color: 'white',
            font: {
              weight: 'bold',
              size: 11,
            },
            formatter: (value: any, context: any) => {
              if ( this.chartConfig?.dataLabel &&
                ['both', 'percentage', 'value'].includes(
                  this.chartConfig?.dataLabel || ''
                )
              ) {
                const meta = context.dataset.data._chartjs.listeners[0].getMeta();

                const { data } = meta;
                const index = meta._dataset.data.findIndex(
                  (el: any) => el === value
                );
                const model = data[index];
                const total = Number(meta.total);
                const startAngle = model.startAngle;
                const endAngle = model.endAngle;
                // calculating Area
                const angle = endAngle - startAngle;
                const donutSectorArea =
                  (angle / 2) *
                  (model.outerRadius - model.innerRadius) *
                  (model.outerRadius + model.innerRadius);

                const percent = (value / total) * 100;
                if (
                  value !== 0 &&
                  ((donutSectorArea && donutSectorArea > 1000) ||
                    (!donutSectorArea && percent > 1.5))
                ) {
                  if (this.isProgress){
                    return '';
                  }
                  const formatedValue = this.formatNumberWithCommas(value);
                  return (this.chartConfig?.dataLabel === 'both' && !this.isPercentage) ?
                    `${formatedValue} (${percent.toFixed(2)}%)` :
                    (this.chartConfig?.dataLabel === 'percentage' && !this.isPercentage) ?
                      `${percent.toFixed(2)}%` :
                      (this.chartConfig?.dataLabel === 'value') ?
                        formatedValue :
                        this.isPercentage ? `${value}%` : '';
                } else {
                  return '';
                }
              }
              return '';
            },
          },
          legend: {
            position: 'bottom',
            labels: {
              display: true,
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 15,
              boxWidth: 5,
              font: {
                size: 11
              }
            },
          },
          precision: 2,
          tooltip: {
            callbacks: {
              label: (tooltipItem: TooltipItem<'doughnut'>) => {
                if ( !this.chartConfig?.tooltip || !['both', 'percentage', 'value'].includes( this.chartConfig?.tooltip || '' )){
                  return '';
                }
                if (tooltipItem.dataset && tooltipItem.parsed) {
                  const dataset = tooltipItem.dataset;
                  const total = dataset.data.reduce(
                    (acc: number, val: number) => Number(acc) + Number(val),
                    0
                  );

                  const currentValue = dataset.data[tooltipItem.dataIndex || 0];
                  const percentage = ((currentValue / total) * 100).toFixed(2);
                  const tooltipValue = (this.chartConfig?.tooltip === 'both' && !this.isPercentage) ?
                    `${currentValue} (${percentage}%)` :
                    (this.chartConfig?.tooltip === 'percentage' && !this.isPercentage) ?
                      `${percentage}%` :
                      (this.chartConfig?.tooltip === 'value') ?
                        currentValue :
                        this.isPercentage ? `${currentValue}%` : '';
                  return `${tooltipItem.label} : ${tooltipValue} `;
                }
                return '';
              },
            },
          },
        } as any,
        centerText: {
          // this custom before using it we need to register this key refer line no 100-103
          show: this.chartConfig.centerText,
          calculate: (values: number[]) => {
           const symbol: string = this.isPercentage ? '%' : '';
           if (this.isProgress){
             const total: number = values.reduce((p, c) => Number(p) + Number(c), 0);
             const percentage: number = (values[0] / total) * 100;
             return values[0] + ' (' + percentage.toFixed(2) + '%)';
          }
           const totalNumber = values.reduce((p: number, c: number) => Number(p) + Number(c), 0);
           if (symbol !== '%') {
             return this.formatNumberWithCommas(totalNumber);
           }
           return `${totalNumber}${symbol}`;
          },
          font: 'bold 14px Arial',
          color: 'black',
          title: {
            font: '11px Arial',
            color: 'black',
            text: this.isPercentage ? 'Total Percentage' : this.isProgress ? '' : 'Total',
            yOffset: -20,
          },
        }
      } as any,
    });
  }

  formatNumberWithCommas(value: any): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

  ngAfterViewInit(): void {
    if (this.dChartCanvas?.nativeElement){
    const context = this.dChartCanvas.nativeElement.getContext('2d');
    if (!context) {
      return;
    }
    this.dChartContext = context;
    this.createdChart();
    }
  }
  public chartClicked(e: any): void {
  }

  public chartHovered(e: any): void {
  }
  getLabelForChart(data: any): void {
    data?.forEach((el: any) => {
      if (el?.question_label) {
        const question = el?.question_label;
        this.customLabel.push(question);
      }
    });
    this.showNoData = this.customData.every((element: any) => element === '0' || element === '0.00');
  }
  getDataForChart(data: any): void {
    data?.forEach((el: any) => {
      if (el?.total_count) {
        el.total_count = el?.value_format === 'percentage' ? parseFloat(el?.total_count).toFixed(2) : el?.total_count;
        this.customData.push(el?.total_count);
      }
    });
    this.isPercentage = data.some(
      (el: any) => el?.value_format === 'percentage'
    );
    this.showNoData = this.customData.every((element: any) => element === '0' || element === '0.00');

    this.isProgress = data.some((el: any) => el?.name === 'number_of_factories_working_zdhc_mrsl_conformance');
  }

  convertIntoPercentage(data: any): any[] {
    const sum = data.reduce((a: any, b: any) => a + b);
    const percentages = data.map((e: any) => ((e / sum) * 100).toFixed(2));
    return percentages;
  }
}
